import {
  Button,
  FlexBox,
  JoonUIColor,
  Loader,
  SPACING,
  TextArea,
  TextInput,
  Typography,
} from "@joonapp/web-shared"
import { useState } from "react"
import toast from "react-hot-toast"

import MarkdownResultCard from "./MarkdownResultCard"
import {
  copyText,
  trackGenerationCompleted,
  trackGenerationFailed,
  trackGenerationStarted,
  trackResultCopied,
} from "./sharedTools"
import ToolPage from "./ToolPage"
import { generateInterventionStrategies } from "../../networking/tools"
import { requireAuth } from "../../util/auth"

const TOOL_ID = "intervention_strategy_generator"

const InterventionStrategyGenerator = requireAuth(() => {
  const [targetBehavior, setTargetBehavior] = useState("")
  const [clientCharacteristics, setClientCharacteristics] = useState("")
  const [environmentalFactors, setEnvironmentalFactors] = useState("")
  const [previousInterventions, setPreviousInterventions] = useState("")
  const [strategies, setStrategies] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const generate = async () => {
    setLoading(true)
    setError(null)
    const t0 = Date.now()
    trackGenerationStarted(TOOL_ID, {
      target_behavior_length: targetBehavior.length,
      has_client_characteristics: !!clientCharacteristics.trim(),
      has_environmental_factors: !!environmentalFactors.trim(),
      has_previous_interventions: !!previousInterventions.trim(),
    })
    try {
      const data = await generateInterventionStrategies({
        targetBehavior,
        clientCharacteristics: clientCharacteristics.trim() || "Not provided",
        environmentalFactors: environmentalFactors.trim() || "Not provided",
        previousInterventions: previousInterventions.trim() || "Not provided",
      })
      setStrategies(data.strategies)
      trackGenerationCompleted(TOOL_ID, { duration_ms: Date.now() - t0 })
    } catch (e: any) {
      setError(`Failed to generate strategies: ${e.message}`)
      trackGenerationFailed(TOOL_ID, { error_code: e.message })
    }
    setLoading(false)
  }

  const onCopy = async () => {
    await copyText(strategies)
    toast.success("Copied to clipboard")
    trackResultCopied(TOOL_ID)
  }

  return (
    <ToolPage
      toolId={TOOL_ID}
      title="Intervention Strategy Generator"
      description="Describe a target behavior. We'll generate 3 ABA strategies with protocols, data plans, and contingencies."
    >
      <Field label="Target behavior or skill (required)">
        <TextInput
          name="Target behavior"
          value={targetBehavior}
          onChange={(e) => setTargetBehavior(e.target.value)}
          fullWidth
        />
      </Field>
      <Field label="Client characteristics (optional)">
        <TextArea
          name="Client characteristics"
          value={clientCharacteristics}
          onChange={(e) => setClientCharacteristics(e.target.value)}
          placeholder="Age, diagnosis, current skill level…"
          fullWidth
        />
      </Field>
      <Field label="Environmental factors (optional)">
        <TextArea
          name="Environmental factors"
          value={environmentalFactors}
          onChange={(e) => setEnvironmentalFactors(e.target.value)}
          fullWidth
        />
      </Field>
      <Field label="Previous interventions tried (optional)">
        <TextArea
          name="Previous interventions"
          value={previousInterventions}
          onChange={(e) => setPreviousInterventions(e.target.value)}
          fullWidth
        />
      </Field>
      <Button
        text="Generate ABA intervention strategies"
        onClick={generate}
        disabled={loading || !targetBehavior}
        fullWidth
      />
      {loading && (
        <FlexBox justify="center">
          <Loader size={24} color={JoonUIColor.semantic.primary} />
        </FlexBox>
      )}
      {error && (
        <Typography variant="bodySmall" color={JoonUIColor.semantic.alert}>
          {error}
        </Typography>
      )}
      {strategies && (
        <MarkdownResultCard
          title="Generated ABA intervention strategies"
          markdown={strategies}
          onCopy={onCopy}
        />
      )}
    </ToolPage>
  )
})

const Field = ({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) => (
  <FlexBox direction="column" gap={SPACING.space1}>
    <Typography variant="bodyBold" textAlign="left">
      {label}
    </Typography>
    {children}
  </FlexBox>
)

export default InterventionStrategyGenerator
